import { QueryClient } from '@tanstack/react-query';

/**
 * ENTERPRISE QUERY CLIENT CONFIGURATION
 * Optimized for high-traffic e-commerce with intelligent caching
 * Strategy: Cache everything, validate in background, prioritize user experience
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // CRITICAL: Keep data in cache longer to reduce API calls
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - keep in memory before garbage collection
      
      // Automatic background refetch when user returns to tab
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      
      // Smart retry strategy
      retry: 1, // Retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Never throw errors - graceful degradation
      throwOnError: false,
    },
    mutations: {
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/**
 * CACHE KEYS STRUCTURE
 * Organized by domain for efficient invalidation
 */
export const queryKeys = {
  products: {
    all: ['products'] as const,
    lists: () => [...queryKeys.products.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
    featured: () => [...queryKeys.products.lists(), 'featured'] as const,
    bestsellers: () => [...queryKeys.products.lists(), 'bestsellers'] as const,
  },
  categories: {
    all: ['categories'] as const,
    lists: () => [...queryKeys.categories.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.categories.lists(), filters] as const,
  },
  banners: {
    all: ['banners'] as const,
  },
  videos: {
    all: ['videos'] as const,
  },
  settings: {
    all: ['settings'] as const,
  },
  auth: {
    all: ['auth'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const,
  },
  cart: {
    all: ['cart'] as const,
  },
};
